<!-- Now — is it going to rain on me, and when.

     One question, one screen, and the trace answers it. Everything else here is
     a caption on the trace: the conditions above it, the sentence below it, and
     the one control that fixes the only thing that can be wrong. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="now">
      <div class="sheet">
        <ConditionsHead
          v-if="place"
          :location="place"
          :observation="observation"
          :warning="activeWarning"
          @open-warning="openWarning"
        />

        <!-- No place yet. The masthead still stands and the paper still shows.
             The reason is carried ONCE, by the summary line below the trace,
             where the forecast sentence lives when there is one — saying it
             here as well printed the same sentence twice on one screen and
             pushed the controls off a short phone. -->
        <header v-else class="unlocated">
          <h1>Overhead</h1>
          <p class="meta">Rain, for one place.</p>
        </header>

        <!-- Tappable only when there is a radar worth opening. A button that
             navigates to an empty screen is a worse answer than no button. -->
        <button
          v-if="place"
          class="trace-link"
          type="button"
          @click="router.push({ name: 'radar' })"
        >
          <RainTrace
            :hours="traceHours"
            :observed-at="observation?.recordedAt"
            :threshold-mm="thresholdMm"
            :now="nowIso"
          />
          <span class="sr-only">Open the radar</span>
        </button>
        <RainTrace
          v-else
          :hours="[]"
          :threshold-mm="thresholdMm"
          :now="nowIso"
        />

        <p class="summary">{{ place ? forecastSummary : permissionLine }}</p>

        <div class="actions">
          <button class="act" type="button" :disabled="busy" @click="locate">
            {{ actionLabel }}
          </button>
          <button class="act quiet" type="button" @click="router.push({ name: 'settings' })">
            Settings
          </button>
        </div>

        <!-- Said plainly, on the screen the numbers appear on — and said in one
             line, because it is a disclosure and not an apology. -->
        <p class="source">Sample data — no forecast provider connected.</p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import ConditionsHead from '../components/ConditionsHead.vue';
import RainTrace from '../components/RainTrace.vue';
import '../chrome.js';
import {
  place, observation, traceHours, activeWarning,
  thresholdMm, permissionState, forecastSummary, nowIso,
  useMyLocation, refresh,
} from '../weather.js';

const router = useRouter();
const busy = ref(false);

// Three states, not two. "Not yet asked" is not the same as "said no", and an
// app that treats them alike either nags someone who declined or never asks.
const permissionLine = computed(() => {
  if (permissionState.value === 'denied') {
    return 'Location is turned off for Overhead. Turn it on in your device '
      + 'settings and the trace comes back.';
  }
  return 'No forecast yet. Overhead needs your location to draw the trace.';
});

const actionLabel = computed(() => {
  if (busy.value) return permissionState.value === 'granted' ? 'Refreshing…' : 'Locating…';
  if (permissionState.value === 'granted') return 'Refresh';
  if (permissionState.value === 'denied') return 'Try again';
  return 'Use my location';
});

// The prompt fires HERE, from a control someone pressed — never at launch.
async function locate() {
  busy.value = true;
  try {
    if (permissionState.value === 'granted') await refresh();
    else await useMyLocation();
  } finally {
    busy.value = false;
  }
}

// BY NAME, not by path: the shell decides where a screen is mounted, so a
// literal path can stop matching without anything erroring.
const openWarning = (id) => router.push({ name: 'warning-detail', params: { id } });
</script>

<style scoped>
.now {
  --background: var(--oh-paper, #dfe3d1);
  --ink: var(--oh-ink, #1b2540);
}
.sheet {
  --ink: var(--oh-ink, #1b2540);
  background: var(--oh-paper, #dfe3d1);
  min-height: 100%;
}
.unlocated { padding: env(safe-area-inset-top, 0px) 14px 0; }
.unlocated h1 {
  margin: 16px 0 0;
  font-family: var(--oh-serif, Georgia, serif);
  font-style: italic;
  font-weight: 400;
  font-size: 27px;
  line-height: 1.1;
  color: var(--ink);
}
.meta {
  margin: 3px 0 12px;
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 11px;
  letter-spacing: 0.06em;
  color: color-mix(in srgb, var(--ink) 60%, transparent);
}
.trace-link {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.trace-link:focus-visible { outline: 2px solid var(--ink); outline-offset: -3px; }
.summary {
  margin: 0;
  padding: 10px 14px 12px;
  font-family: var(--oh-serif, Georgia, serif);
  font-size: 16px;
  line-height: 1.35;
  color: var(--ink);
}
.actions { display: flex; gap: 8px; padding: 0 14px 12px; }
.act {
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid color-mix(in srgb, var(--ink) 35%, transparent);
  border-radius: 2px;
  background: transparent;
  color: var(--ink);
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
}
.act:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.act[disabled] { opacity: 0.5; cursor: default; }
.act.quiet { border-color: transparent; opacity: 0.65; }
.source {
  margin: 0;
  padding: 0 14px calc(16px + env(safe-area-inset-bottom, 0px));
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 10px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: color-mix(in srgb, var(--ink) 45%, transparent);
}
</style>
